<template>
    <div class="ap-wrap">
        <!-- En-tête -->
        <div class="ds-page-head">
            <div>
                <h1 class="ds-page-head__title">
                    <i class="ri-shopping-basket-2-line"></i> Audit paniers Scan &amp; Go (supermarché)
                </h1>
                <p class="ds-page-head__sub">
                    Ce que les clients ont <b>envoyé</b> par QR/téléphone vs ce qui a été
                    réellement <b>facturé</b> en caisse. Aide à suivre l'usage et les abandons.
                </p>
            </div>
        </div>

        <!-- Filtres -->
        <div class="cf-card">
            <div class="ap-filters">
                <div class="ap-field">
                    <label class="fa2-label">Du</label>
                    <input type="date" class="fa2-inp" v-model="dateDebut">
                </div>
                <div class="ap-field">
                    <label class="fa2-label">Au</label>
                    <input type="date" class="fa2-inp" v-model="dateFin">
                </div>
                <div class="ap-field">
                    <label class="fa2-label">Magasin (pid, optionnel)</label>
                    <input type="text" class="fa2-inp" v-model="mag" placeholder="Tous les magasins">
                </div>
                <div class="ap-field ap-field--btn">
                    <button class="ti-btn ti-btn-primary-full !font-medium !text-[0.8rem] ap-search-btn" :disabled="loading" @click="charger">
                        <i class="ri-search-line" :class="{ 'animate-spin': loading }"></i>
                        <span>Rechercher</span>
                    </button>
                </div>
            </div>
        </div>

        <!-- Résumé -->
        <div v-if="loaded && !erreur" class="ap-kpi-grid">
            <div class="cf-card cf-card--piece ap-kpi">
                <span class="ap-kpi__label">Paniers reçus</span>
                <p class="ap-kpi__value">{{ resume.recu + resume.facture + resume.abandonne }}</p>
            </div>
            <div class="cf-card cf-card--piece ap-kpi">
                <span class="ap-kpi__label">Facturés</span>
                <p class="ap-kpi__value cf-ok">{{ resume.facture }}</p>
            </div>
            <div class="cf-card cf-card--piece ap-kpi">
                <span class="ap-kpi__label">Non facturés (abandon)</span>
                <p class="ap-kpi__value cf-attente">{{ resume.recu + resume.abandonne }}</p>
            </div>
            <div class="cf-card cf-card--piece ap-kpi">
                <span class="ap-kpi__label">Montant facturé</span>
                <p class="ap-kpi__value">{{ fmt(resume.montantFacture) }}</p>
            </div>
        </div>

        <div v-if="loading && !loaded" class="ap-state">
            <i class="ri-loader-4-line animate-spin ap-state__icon cf-faint"></i>
            <p class="ap-state__txt">Chargement…</p>
        </div>

        <div v-else-if="erreur" class="cf-note cf-note--danger" role="alert">
            <i class="ri-error-warning-line"></i> {{ erreur }}
        </div>

        <div v-else-if="loaded && !paniers.length" class="ap-state">
            <i class="ri-shopping-basket-2-line ap-state__icon cf-faint"></i>
            <p class="ap-state__txt">Aucun panier scanné sur cette période.</p>
        </div>

        <div v-else-if="loaded" class="cf-card cf-card--flush">
            <div class="table-scroll-wrap">
                <table class="doc-table">
                    <thead>
                        <tr>
                            <th class="th"></th>
                            <th class="th">Date</th>
                            <th class="th">Magasin</th>
                            <th class="th">Voie</th>
                            <th class="th th--center">Art. envoyés</th>
                            <th class="th">Statut</th>
                            <th class="th">Document</th>
                            <th class="th th--center">Montant</th>
                        </tr>
                    </thead>
                    <tbody>
                        <template v-for="(p, i) in paniers" :key="i">
                            <tr class="table-row ap-row-click" @click="basculerDetail(i)">
                                <td class="td"><i :class="ouvert === i ? 'ri-arrow-down-s-line' : 'ri-arrow-right-s-line'"></i></td>
                                <td class="td">{{ formatDateTime(p.created_at) }}</td>
                                <td class="td">{{ p.mag || '-' }}</td>
                                <td class="td">
                                    <span class="badge" :class="p.source === 'ws' ? 'bg-info/10 text-info' : 'bg-secondary/10 text-secondary'">
                                        {{ p.source === 'ws' ? 'Téléphone' : 'QR' }}
                                    </span>
                                </td>
                                <td class="td td--center">{{ p.nb_articles }}</td>
                                <td class="td">
                                    <span class="badge" :class="p.statut === 'facture'
                                        ? 'bg-success/10 text-success'
                                        : 'bg-warning/10 text-warning'">
                                        {{ p.statut === 'facture' ? 'Facturé' : 'Non facturé' }}
                                    </span>
                                </td>
                                <td class="td">{{ p.docnum || '-' }}</td>
                                <td class="td td--center">
                                    <span class="cf-strong">{{ p.statut === 'facture' ? fmt(p.montant) : '-' }}</span>
                                </td>
                            </tr>
                            <tr v-if="ouvert === i">
                                <td colspan="8" class="ap-detail-cell">
                                    <div class="ap-detail-grid">
                                        <div>
                                            <h6 class="ap-detail-title">
                                                <i class="ri-smartphone-line"></i> Envoyé par le client
                                            </h6>
                                            <table class="ap-sub-table">
                                                <thead><tr><th>Code-barres</th><th class="ap-right">Qté</th></tr></thead>
                                                <tbody>
                                                    <tr v-for="(l, j) in lignesClient(p)" :key="'c'+j">
                                                        <td class="ap-mono">{{ l[0] }}</td>
                                                        <td class="ap-right">{{ l[1] }}</td>
                                                    </tr>
                                                    <tr v-if="!lignesClient(p).length"><td colspan="2" class="cf-faint">-</td></tr>
                                                </tbody>
                                            </table>
                                        </div>
                                        <div>
                                            <h6 class="ap-detail-title">
                                                <i class="ri-store-2-line"></i> Facturé en caisse
                                            </h6>
                                            <table class="ap-sub-table">
                                                <thead><tr><th>Article</th><th class="ap-right">Qté</th><th class="ap-right">Montant</th></tr></thead>
                                                <tbody>
                                                    <tr v-for="(l, j) in lignesCaisse(p)" :key="'f'+j">
                                                        <td>{{ l.nom || l.ean }}</td>
                                                        <td class="ap-right">{{ l.qte }}</td>
                                                        <td class="ap-right">{{ fmt(l.montant) }}</td>
                                                    </tr>
                                                    <tr v-if="!lignesCaisse(p).length"><td colspan="3" class="cf-faint">Non facturé</td></tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        </template>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
</template>

<script>
    /* eslint-disable */
    const axios =require('axios');
    import * as JwtService from "@/common/jwt.service";
    import { randomString } from "@/allfunction";

    /**
     * Onglet Paramètres - audit des paniers Scan & Go supermarché.
     *
     * Lit la route PROTÉGÉE scango paniersaudit (btkpid de la session). Données de
     * scango_paniers : « envoyé » (lignes_client, posé à l'ingestion caisse) vs
     * « facturé » (lignes_caisse, posé à la comptabilisation). Le panier est
     * capturé APRÈS le scan à la caisse, en HTTP - indépendant du WebSocket.
     */
    export default {
        name: "auditpaniers",
        data() {
            const auj = this._aujourdhui();
            return {
                loading: false,
                loaded: false,
                erreur: "",
                paniers: [],
                resume: { recu: 0, facture: 0, abandonne: 0, montantFacture: 0 },
                ouvert: -1,
                mag: "",
                dateDebut: auj,
                dateFin: auj,
            };
        },
        methods: {
            _aujourdhui() {
                const d = new Date();
                const p = (n) => String(n).padStart(2, "0");
                return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
            },

            _headers() {
                return {
                    "X-Requested-With": "XMLHttpRequest",
                    "Accept": "application/json",
                    "Content-Type": "application/json",
                    "Authorization": "Bearer " + JwtService.getToken(),
                    "csrftoken": this.$store.state.appsociete,
                };
            },

            fmt(v) {
                const n = parseFloat(v) || 0;
                try { return n.toLocaleString("fr-FR"); } catch (e) { return String(n); }
            },

            formatDateTime(v) {
                if (!v) return "-";
                const d = new Date(v);
                if (isNaN(d.getTime())) return String(v);
                const p = (n) => String(n).padStart(2, "0");
                return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
            },

            basculerDetail(i) { this.ouvert = this.ouvert === i ? -1 : i; },

            /** [[ean, qte], ...] envoyés par le client (LONGTEXT JSON, tolérant). */
            lignesClient(p) {
                try {
                    const arr = typeof p.lignes_client === "string" ? JSON.parse(p.lignes_client) : p.lignes_client;
                    return Array.isArray(arr) ? arr : [];
                } catch (e) { return []; }
            },

            /** [{ean,nom,qte,pu,montant}, ...] réellement facturés. */
            lignesCaisse(p) {
                try {
                    const arr = typeof p.lignes_caisse === "string" ? JSON.parse(p.lignes_caisse) : p.lignes_caisse;
                    return Array.isArray(arr) ? arr : [];
                } catch (e) { return []; }
            },

            async charger() {
                this.loading = true;
                this.erreur = "";
                this.ouvert = -1;
                const url = process.env.VUE_APP_API_URL
                    + "/egl745874pscango771714dfe/caisse/othetdskd8755dsdhisd/paniersaudit/"
                    + randomString();
                try {
                    const res = await axios.post(url, {
                        mag: this.mag || "",
                        dateDebut: this.dateDebut || "",
                        dateFin: this.dateFin || "",
                    }, { headers: this._headers(), withCredentials: true });
                    const d = res.data || {};
                    if (d.success && d.m) {
                        this.paniers = Array.isArray(d.m.paniers) ? d.m.paniers : [];
                        this.resume = Object.assign(
                            { recu: 0, facture: 0, abandonne: 0, montantFacture: 0 },
                            d.m.resume || {}
                        );
                    } else {
                        this.erreur = "Accès refusé ou module Scan & Go non configuré.";
                    }
                    this.loaded = true;
                } catch (e) {
                    console.error("[auditpaniers] chargement:", e);
                    this.erreur = "Impossible de charger l'audit des paniers.";
                    this.loaded = true;
                } finally {
                    this.loading = false;
                }
            },
        },
    };
</script>

<style scoped>
    .ap-wrap { padding: 0.25rem; }
</style>
